import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditNotesContent } from './content'
import { EditNotesControls } from './controls'

export enum Input {
  Notes = 'notes',
}

export const editAppointmentNotesSchema = z.object({
  [Input.Notes]: z.string()
    .max(2048, { message: 'Notes must be less than 2048 characters.' })
    .optional(),
})

export type EditAppointmentNotesSchema = z.infer<typeof editAppointmentNotesSchema>

type EditAppointmentNotesFormContextType = {
  onSubmit: (values: EditAppointmentNotesSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditAppointmentNotesFormContext = createContext<EditAppointmentNotesFormContextType | undefined>(undefined)

type EditAppointmentNotesFormProps = EditAppointmentNotesFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditAppointmentNotesSchema>,
}

export function EditAppointmentNotesForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditAppointmentNotesFormProps) {

  const context: EditAppointmentNotesFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditAppointmentNotesFormContext.Provider value={context}>
      <Form<EditAppointmentNotesSchema, typeof editAppointmentNotesSchema>
        schema={editAppointmentNotesSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditAppointmentNotesFormContext.Provider>
  )
}

export function useEditAppointmentNotesForm() {
  const context = useContext(EditAppointmentNotesFormContext)
  if (context === undefined) {
    throw new Error('useEditAppointmentNotesForm must be used within an EditAppointmentNotesFormProvider')
  }
  return context
}

// Compound components
EditAppointmentNotesForm.Content = EditNotesContent
EditAppointmentNotesForm.Controls = EditNotesControls
